// ============================================
// COMPONENT - Modal registrar movimiento de caja (egreso/ingreso)
// ============================================

import { useState } from 'react';
import { Button } from '@almacenes/ui';
import type { RegistrarMovimientoDto } from '@almacenes/core';
import { MdClose, MdTrendingDown, MdTrendingUp } from 'react-icons/md';
import { formatMontoInput } from '../utils/formato';

interface RegistrarMovimientoModalProps {
  onClose: () => void;
  onConfirmar: (dto: RegistrarMovimientoDto) => void;
  isLoading: boolean;
}

type Categoria = 'EGRESO' | 'INGRESO';

interface RazonOpcion {
  value: string;
  label: string;
  tipoBackend: RegistrarMovimientoDto['tipo'];
}

const razonesPorCategoria: Record<Categoria, RazonOpcion[]> = {
  EGRESO: [
    { value: 'RETIRO_EFECTIVO', label: 'Retiro de efectivo', tipoBackend: 'RETIRO' },
    { value: 'PAGO_PROVEEDOR', label: 'Pago a proveedor', tipoBackend: 'GASTO' },
    { value: 'PAGO_SERVICIO', label: 'Pago de servicio/gasto operativo', tipoBackend: 'GASTO' },
    { value: 'OTRO_EGRESO', label: 'Otro', tipoBackend: 'GASTO' },
  ],
  INGRESO: [
    { value: 'INGRESO_EXTRA', label: 'Ingreso extra', tipoBackend: 'INGRESO_EXTRA' },
    { value: 'AJUSTE_VUELTO', label: 'Ajuste de vuelto', tipoBackend: 'INGRESO_EXTRA' },
    { value: 'OTRO_INGRESO', label: 'Otro', tipoBackend: 'INGRESO_EXTRA' },
  ],
};

const categorias: Array<{
  value: Categoria;
  label: string;
  icono: typeof MdTrendingDown;
  colorClaseSeleccionado: string;
}> = [
  { value: 'EGRESO', label: 'Egreso', icono: MdTrendingDown, colorClaseSeleccionado: 'border-red-500 bg-red-50 text-red-700' },
  { value: 'INGRESO', label: 'Ingreso', icono: MdTrendingUp, colorClaseSeleccionado: 'border-green-500 bg-green-50 text-green-700' },
];

export const RegistrarMovimientoModal = ({ onClose, onConfirmar, isLoading }: RegistrarMovimientoModalProps) => {
  const [categoria, setCategoria] = useState<Categoria>('EGRESO');
  const [razonValue, setRazonValue] = useState<string>(razonesPorCategoria.EGRESO[0].value);
  const [montoDisplay, setMontoDisplay] = useState('');
  const [montoRaw, setMontoRaw] = useState('');
  const [detalle, setDetalle] = useState('');
  const [errors, setErrors] = useState<{ monto?: string }>({});

  const razones = razonesPorCategoria[categoria];

  const handleCategoriaChange = (nueva: Categoria) => {
    setCategoria(nueva);
    setRazonValue(razonesPorCategoria[nueva][0].value);
  };

  const handleMontoChange = (value: string) => {
    const soloNumeros = value.replace(/\D/g, '');
    setMontoRaw(soloNumeros);
    setMontoDisplay(formatMontoInput(soloNumeros));
    if (errors.monto) setErrors({});
  };

  const handleConfirmar = () => {
    if (!montoRaw || Number(montoRaw) <= 0) {
      setErrors({ monto: 'El monto debe ser mayor a cero' });
      return;
    }

    const razon = razones.find((r) => r.value === razonValue) ?? razones[0];
    const glosa = detalle.trim() ? `${razon.label}: ${detalle.trim()}` : razon.label;

    onConfirmar({ tipo: razon.tipoBackend, monto: Number(montoRaw), glosa });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold" style={{ color: '#11194F' }}>
            Registrar movimiento
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <MdClose className="text-2xl" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Tipo <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {categorias.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => handleCategoriaChange(c.value)}
                  disabled={isLoading}
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border text-sm font-semibold transition-all ${
                    categoria === c.value ? c.colorClaseSeleccionado : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <c.icono className="text-lg" />
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Razón <span className="text-red-500">*</span>
            </label>
            <select
              value={razonValue}
              onChange={(e) => setRazonValue(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
              disabled={isLoading}
            >
              {razones.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Monto (CLP) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={montoDisplay}
                onChange={(e) => handleMontoChange(e.target.value)}
                placeholder="5.000"
                className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors.monto ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                }`}
                disabled={isLoading}
              />
            </div>
            {errors.monto && <p className="text-red-500 text-sm mt-1">{errors.monto}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Detalle <span className="text-gray-400 font-normal">(opcional)</span>
            </label>
            <input
              type="text"
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Ej: factura 123, nombre del proveedor..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="flex gap-3 pt-6">
          <Button variant="secondary" onClick={onClose} disabled={isLoading} className="flex-1">
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmar} isLoading={isLoading} disabled={isLoading} className="flex-1">
            Registrar
          </Button>
        </div>
      </div>
    </div>
  );
};
